import React, { useState } from 'react';
import { motion } from 'framer-motion';
import emailjs from '@emailjs/browser';

// Configuration EmailJS
const EMAILJS_SERVICE_ID = 'service_5a8t13o';
const EMAILJS_TEMPLATE_ID = 'template_i2aa6wr';
const EMAILJS_PUBLIC_KEY = 'zMoNd0pSIrPjf8bSK';

// Initialiser EmailJS
emailjs.init(EMAILJS_PUBLIC_KEY);

const ContactSection = () => {
  const [formData, setFormData] = useState({
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [status, setStatus] = useState({
    submitted: false,
    submitting: false,
    info: { error: false, msg: null }
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus(prevStatus => ({ ...prevStatus, submitting: true }));
    
    try {
      console.log('Envoi via EmailJS ContactSection:', formData);
      
      // Préparer les données pour EmailJS
      const templateParams = {
        from_name: 'Contact depuis le site',
        from_email: formData.email,
        phone: formData.phone || 'Non renseigné',
        service: formData.service,
        message: formData.message,
        to_email: 'contact@aiko-agency.fr'
      };
      
      // Envoi via EmailJS
      const result = await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        templateParams
      );
      
      console.log('Email envoyé avec succès:', result);
      setStatus({
        submitted: true,
        submitting: false,
        info: { error: false, msg: 'Votre message a été envoyé avec succès !' }
      });
      setFormData({
        email: '',
        phone: '',
        service: '',
        message: ''
      });
    } catch (error) {
      console.error('Erreur lors de l\'envoi:', error);
      setStatus({
        submitted: false,
        submitting: false,
        info: { error: true, msg: 'Une erreur est survenue lors de l\'envoi du formulaire.' }
      });
    }
  };

  return (
    <div className="relative overflow-hidden py-16 md:py-24 my-8 md:my-12" id="contact">
      <div className="container mx-auto px-4 md:px-8 lg:px-16">
        <div className="flex flex-col lg:flex-row items-center gap-12">
          {/* Colonne de gauche - Texte */}
          <div className="lg:w-1/2">
            <motion.h2 
            className="text-2xl md:text-3xl lg:text-4xl font-medium mb-6 text-[#0a2540] leading-tight"
            initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              Aidez-nous à adresser votre demande à la bonne personne
            </motion.h2>
            
            <motion.p 
              className="text-gray-600 mb-6"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
            >
              Nous sommes là pour vous accompagner dans votre projet digital. Que vous ayez besoin d'un site web, d'une identité visuelle ou d'une solution d'automatisation, notre équipe d'experts est prête à vous aider.<br></br><br></br> Parlez-nous de votre projet et nous vous répondrons dans les plus brefs délais pour discuter de la meilleure approche pour votre entreprise.
            </motion.p>
          </div>
          
          {/* Colonne de droite - Formulaire */}
          <div className="lg:w-1/2">
            <motion.div 
              className="bg-white rounded-lg shadow-lg p-8"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true }}
            >
              {status.submitted ? (
                <div className="text-center py-12">
                  <svg className="w-16 h-16 text-green-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  <h3 className="text-xl font-medium mb-2 text-[#0a2540]">Message envoyé !</h3>
                  <p className="text-gray-600">{status.info.msg}</p>
                  <button
                    onClick={() => setStatus({ submitted: false, submitting: false, info: { error: false, msg: null } })}
                    className="mt-6 px-5 py-2 rounded-[40px] bg-[#5046E5] text-white font-medium hover:bg-[#4038c2] transition-all duration-300 shadow-lg text-sm"
                  >
                    Envoyer un nouveau message
                  </button>
                </div>
              ) : (
                <>
                  <h3 className="text-xl font-medium mb-4 text-[#0a2540]">
                    Nous souhaitons répondre au mieux à votre demande
                  </h3>
                  
                  <p className="text-gray-600 mb-6">
                    Pour cela, nous avons besoin de quelques informations.
                  </p>
                  
                  {status.info.error && (
                    <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md">
                      {status.info.msg}
                    </div>
                  )}
                  
                  <form onSubmit={handleSubmit}>
                    <div className="mb-4">
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                        Adresse e-mail professionnelle
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="jeanne@exemple.com"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#5046E5]"
                        required
                        disabled={status.submitting}
                      />
                    </div>
                    
                    <div className="mb-4">
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                        Téléphone (non obligatoire)
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="06 12 34 56 78"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#5046E5]"
                        disabled={status.submitting}
                      />
                    </div>
                    
                    <div className="mb-4">
                      <label htmlFor="service" className="block text-sm font-medium text-gray-700 mb-1">
                        Service
                      </label>
                      <div className="relative">
                        <select
                          id="service"
                          name="service"
                          value={formData.service}
                          onChange={handleChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md appearance-none focus:outline-none focus:ring-2 focus:ring-[#5046E5]"
                          required
                          disabled={status.submitting}
                        >
                          <option value="" disabled>Sélectionnez un service</option>
                          <option value="site-internet">Création site internet</option>
                          <option value="logo">Création de logo</option>
                          <option value="hebergement">Hébergement</option>
                          <option value="automatisation">Automatisation</option>
                          <option value="autre">Autre</option>
                        </select>
                        <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                          <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </div>
                      </div>
                    </div>
                    
                    <div className="mb-6">
                      <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                        Message
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Décrivez votre projet ou votre demande..."
                        rows="4"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#5046E5]"
                        required
                        disabled={status.submitting}
                      ></textarea>
                    </div>
                    
                    <button
                      type="submit"
                      className="w-auto ml-auto px-5 py-2 rounded-[40px] bg-[#5046E5] text-white font-medium hover:bg-[#4038c2] transition-all duration-300 shadow-lg text-sm inline-flex items-center"
                      disabled={status.submitting}
                    >
                      {status.submitting ? 'Envoi en cours...' : 'Envoyer'}
                      <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
                      </svg>
                    </button>
                  </form>
                </>
              )}
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactSection;
